import React from "react";
//#region types
import { CurrencyEnums } from "models/enums";
import { AuditSheetSummaryItem, AuditSheetSummaryListProps } from "models/propTypes/accountingPropTypes";
import { Amount } from "models/utilTypes/currencyType";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { ButtonBase } from "@mui/material";
import { Icon, TableAsync } from "components/common";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";
import clsx from "clsx";

const hasFiles = (item: AuditSheetSummaryItem) => (item.fileCount ?? 0) > 0;

const AuditSheetSummaryList: React.FC<AuditSheetSummaryListProps> = (props) => {
  const { onSelectRow } = props;
  const isSelectable = !!onSelectRow && props.items.some(hasFiles);

  const selectRow = (item: AuditSheetSummaryItem) => {
    if (onSelectRow && hasFiles(item) && item.rowIndex !== undefined) onSelectRow(item.rowIndex);
  };

  const onFileButtonClick = (item: AuditSheetSummaryItem, event: React.MouseEvent) => {
    event.stopPropagation();
    selectRow(item);
  };

  const formatName = (item: AuditSheetSummaryItem) => {
    if (!isSelectable || !hasFiles(item)) return item.name;

    return (
      <ButtonBase
        className={cssExports.auditSheetItemFileButton}
        aria-current={item.isSelected ? "true" : undefined}
        onClick={onFileButtonClick.bind(null, item)}
        disableRipple
      >
        <span className={cssExports.auditSheetItemFileName}>{item.name}</span>
        <Icon icon={IconDefinitionConstants.WAttachment} className={cssExports.auditSheetItemFileIcon} />
        <span className={cssExports.visuallyHidden}>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.AuditSheetItemHasAttachment)}
        </span>
        {(item.fileCount ?? 0) > 1 && (
          <span className={cssExports.auditSheetItemFileCount}>
            {LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Advisor.AuditSheetItemAttachmentCount,
              undefined,
              { count: item.fileCount }
            )}
          </span>
        )}
      </ButtonBase>
    );
  };

  return (
    <TableAsync<AuditSheetSummaryItem>
      properties={{
        name: {
          description: TranslationKeys.Common.Name,
          isTextSmall: true,
          width: 60,
          formatter: formatName,
          valueToPrint: (item: AuditSheetSummaryItem) => item.name,
        },
        value: {
          description: TranslationKeys.Common.Value,
          width: 40,
          alignment: "right",
          className: cssExports.textBlack,
          formatter: (item: AuditSheetSummaryItem) => new Amount(CurrencyEnums.CurrencyCode.SEK, item.value),
        },
      }}
      disableSort
      pagination={false}
      filter={props.items}
      rowClickHandler={isSelectable ? selectRow : undefined}
      rowClasses={(row: AuditSheetSummaryItem) =>
        clsx(
          row.isBold && cssExports.isBold,
          isSelectable && !hasFiles(row) && cssExports.auditSheetItemWithoutFiles,
          row.isSelected && cssExports.auditSheetItemSelected
        )
      }
    />
  );
};

export default AuditSheetSummaryList;
